import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, ShieldCheck, ChevronRight, Loader2, Check } from "lucide-react";
import { useAuth, API } from "../App";
import { toast } from "sonner";

export const CheckinBetaBanner = ({ forceShow = false, showEmail = false }) => {
  const { token, user } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [show, setShow] = useState(false);
  const [prezzi, setPrezzi] = useState({ annuale: 19.9, mensile: 2.99 });
  const [codice, setCodice] = useState("");
  const [email, setEmail] = useState("");
  const [errore, setErrore] = useState("");
  const [loading, setLoading] = useState(false);
  const [richiesto, setRichiesto] = useState(false);

  useEffect(() => { if (user?.email && !email) setEmail(user.email); }, [user]); // eslint-disable-line

  const richiediInvito = async () => {
    try {
      await axios.post(`${API}/checkin/richiedi-invito`, showEmail ? { email } : {}, { headers: authHeaders });
      setRichiesto(true);
      toast.success("Richiesta inviata! Ti contatteremo via email per l'invito.");
    } catch (_) {
      toast.error("Invio non riuscito. Riprova o scrivi a vitadahost@gmail.com");
    }
  };

  useEffect(() => {
    if (forceShow) { setShow(true); }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/checkin/stato`, { headers: authHeaders });
        if (!alive) return;
        if (!forceShow) setShow(!!r.data.show_banner);
        if (r.data.prezzi) setPrezzi(r.data.prezzi);
      } catch (_) {
        /* non bloccare la dashboard */
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const attiva = async () => {
    setErrore("");
    if (!codice.trim()) { setErrore("Inserisci il codice invito."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/checkin/attiva`, { codice }, { headers: authHeaders });
      toast.success("Plugin attivato su tutte le tue strutture!");
      setShow(false);
    } catch (e) {
      setErrore(e?.response?.data?.detail || "Attivazione non riuscita. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="dashboard-beta-invite-banner"
      className="relative overflow-hidden rounded-3xl bg-zinc-900 text-white p-6 sm:p-7 mb-6 shadow-[0_24px_60px_-28px_rgba(0,0,0,0.6)]"
    >
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-red-500/20 blur-2xl pointer-events-none" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.14em] text-red-300">
          <Sparkles className="w-4 h-4" /> Novità pazzesca in arrivo
        </div>
        <h3 className="mt-2 text-xl sm:text-2xl font-black leading-tight">
          Pre check-in online dal Welcomebook, con invio ad Alloggiati Web e ROSS 1000 tutto
          integrato. Niente più schedine a mano.
        </h3>
        <p className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-emerald-300">
          <ShieldCheck className="w-4 h-4" />
          Disponibile per tutti gli abbonati Hostland, anche per chi ha solo il Welcomebook annuale.
        </p>
        <p className="mt-2 text-xs text-zinc-400 max-w-2xl">
          Per ora si accede solo su invito. Poi sarà un plugin aggiuntivo di Hostland, per tutte le
          tue strutture: {prezzi.annuale?.toFixed(2).replace(".", ",")} € l'anno con l'abbonamento
          annuale, oppure {prezzi.mensile?.toFixed(2).replace(".", ",")} € al mese con il mensile,
          IVA inclusa.
        </p>

        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center max-w-xl">
          {showEmail && (
            <input
              data-testid="beta-signup-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="La tua email per il beta test"
              className="flex-1 bg-white/10 border border-white/15 rounded-xl px-4 py-3 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-red-400/60"
            />
          )}
          {!showEmail && (
          <input
            data-testid="beta-invite-code-input"
            value={codice}
            onChange={(e) => { setCodice(e.target.value); setErrore(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") attiva(); }}
            placeholder="Hai un codice invito? (HOST-XXXX-XXXX)"
            className="flex-1 bg-white/10 border border-white/15 rounded-xl px-4 py-3 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-red-400/60 uppercase"
          />
          )}
          {!showEmail && (
          <button
            data-testid="beta-invite-activate-btn"
            onClick={attiva}
            disabled={loading}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-red-600 hover:bg-red-500 transition-colors px-5 py-3 text-sm font-black disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Attiva <ChevronRight className="w-4 h-4" /></>}
          </button>
          )}
          <button
            data-testid="beta-invite-request-btn"
            onClick={richiediInvito}
            disabled={richiesto || (showEmail && !email.trim())}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-red-600 hover:bg-red-500 border border-white/20 transition-colors px-5 py-3 text-sm font-black disabled:opacity-60 whitespace-nowrap"
          >
            {richiesto ? <><Check className="w-4 h-4" /> Richiesta inviata</> : <>Diventa beta tester</>}
          </button>
        </div>
        {errore && (
          <p data-testid="beta-invite-error" className="mt-2 text-sm font-semibold text-red-300">
            {errore}
          </p>
        )}
      </div>
    </div>
  );
};

export default CheckinBetaBanner;
